import React, { useState, useEffect, useRef } from 'react';
import leadService from '../../services/leadService';
import useEmailFieldValidation from '../../hooks/useEmailFieldValidation';
import { getEmailValidationError, getEmailValidationInputClass } from '../../utils/emailValidation';
import EmailFieldFeedback from '../EmailInput/EmailFieldFeedback';

/**
 * LeadCapturePopup
 *
 * A high-converting email capture popup for "Empty Leg Deals".
 * Completely independent from the auth/signup flow.
 *
 * Props:
 *   isOpen      {boolean}  - Whether the popup is visible
 *   onClose     {function} - Called when user dismisses (X or backdrop)
 *   onSubscribed {function} - Called after successful subscription
 */
const LeadCapturePopup = ({ isOpen, triggerType, onClose, onDontShowAgain, onSubscribed }) => {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState(''); // anti-bot hidden field
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef(null);
  const {
    emailTouched,
    emailError,
    onEmailChange,
    onEmailBlur,
    validateEmailForSubmit,
  } = useEmailFieldValidation({ requiredMessage: 'Please enter your email address.' });

  // Focus the email input when popup opens
  useEffect(() => {
    if (isOpen && status === 'idle') {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, status]);

  // Lock body scroll while popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (status === 'loading' || status === 'success') return;

    const trimmedEmail = email.trim();
    const emailValidationMessage = getEmailValidationError(trimmedEmail, {
      requiredMessage: 'Please enter your email address.',
    });

    validateEmailForSubmit(trimmedEmail);
    if (emailValidationMessage) {
      setErrorMessage(emailValidationMessage);
      return;
    }

    setErrorMessage('');
    setStatus('loading');

    try {
      const response = await leadService.subscribe(trimmedEmail, honeypot, {
        route: window.location.pathname,
        triggerType
      });

      if (response.success) {
        setStatus('success');
        // Notify parent after a short delay so user sees the success state
        setTimeout(() => {
          onSubscribed();
        }, 2500);
      } else {
        setStatus('error');
        setErrorMessage('Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Please try again.');
    }
  };

  const handleEmailChange = (e) => {
    const { value } = e.target;
    setEmail(value);
    onEmailChange(value);
    if (errorMessage) setErrorMessage('');
    if (status === 'error') setStatus('idle');
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-popup-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ animation: 'leadPopupSlideIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close popup"
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors duration-200 shadow-sm"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Hero banner */}
        <div
          className="relative flex flex-col items-center justify-center px-8 pt-10 pb-8 text-center"
          style={{
            background: 'linear-gradient(135deg, #0a1628 0%, #1a3a6b 60%, #0d2a52 100%)'
          }}
        >
          {/* Decorative plane icon */}
          <div className="mb-4 flex items-center justify-center w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
                fill="#FFD200"
              />
            </svg>
          </div>

          <p className="text-[#FFD200] text-xs font-semibold uppercase tracking-widest mb-2">
            Exclusive Offer
          </p>
          <h2
            id="lead-popup-title"
            className="text-white text-2xl font-bold leading-tight mb-3"
            style={{ fontFamily: "'Bahnschrift', 'Arial', sans-serif" }}
          >
            Get Exclusive Private Jet<br />
            <span className="text-[#FFD200]">Empty Leg Deals</span>
          </h2>
          <p className="text-blue-200 text-sm leading-relaxed max-w-xs">
            Be the first to know about last-minute empty leg flights at a fraction of the cost.
          </p>
        </div>

        {/* Form area */}
        <div className="px-8 py-7 bg-white">
          {status === 'success' ? (
            <SuccessState />
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot — hidden from real users, filled by bots */}
              <div
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}
                tabIndex={-1}
              >
                <label htmlFor="lead_website">Website</label>
                <input
                  id="lead_website"
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  autoComplete="off"
                  tabIndex={-1}
                />
              </div>

              {/* Email input */}
              <div className="mb-4">
                <label htmlFor="lead-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <input
                  ref={inputRef}
                  id="lead-email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={(e) => onEmailBlur(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                  disabled={status === 'loading'}
                  aria-describedby={emailError || errorMessage ? 'lead-email-error' : undefined}
                  aria-invalid={!!(emailError || errorMessage)}
                  className={`${getEmailValidationInputClass(
                    'w-full px-4 py-3 rounded-lg border text-sm transition-colors duration-200 outline-none disabled:opacity-60 disabled:cursor-not-allowed bg-white',
                    {
                      touched: emailTouched,
                      error: emailError || errorMessage,
                      value: email,
                      defaultBorderClass: 'border-gray-300 focus:border-[#1a3a6b] focus:ring-2 focus:ring-blue-100',
                    }
                  )} ${emailError || errorMessage ? 'bg-red-50' : ''}`}
                />
                <div id="lead-email-error">
                  <EmailFieldFeedback
                    touched={emailTouched}
                    error={emailError || errorMessage}
                    value={email}
                  />
                </div>
              </div>

              {/* CTA button */}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-3.5 px-6 rounded-lg font-semibold text-sm text-gray-900 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: status === 'loading'
                    ? 'linear-gradient(135deg, #e6be00, #ccaa00)'
                    : 'linear-gradient(135deg, #FFD200, #FFC000)',
                  boxShadow: status === 'loading' ? 'none' : '0 4px 14px rgba(255, 210, 0, 0.4)'
                }}
              >
                {status === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Subscribing...
                  </span>
                ) : (
                  'Subscribe'
                )}
              </button>

              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={onDontShowAgain}
                  className="text-xs text-gray-500 underline hover:text-gray-700 transition-colors"
                >
                  Don&apos;t Show Again
                </button>
              </div>
            </form>
          )}

          {/* Footer */}
          {status !== 'success' && (
            <div className="mt-5 text-center space-y-1">
              <p className="text-xs text-gray-400">
                No spam, ever. Unsubscribe at any time.
              </p>
              <p className="text-xs text-gray-400">
                By subscribing you agree to our{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1a3a6b] underline hover:text-[#0d2a52] transition-colors"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes leadPopupSlideIn {
          from {
            opacity: 0;
            transform: translateY(-24px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Success state shown after subscription
 */
const SuccessState = () => (
  <div className="flex flex-col items-center justify-center py-4 text-center">
    <div
      className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
      style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 13l4 4L19 7"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
    <h3 className="text-lg font-bold text-gray-900 mb-2">You're subscribed!</h3>
    <p className="text-sm text-gray-500 max-w-xs">
      Watch your inbox for exclusive private jet empty leg deals. Welcome aboard!
    </p>
  </div>
);

export default LeadCapturePopup;
